import { CompositionStage, useComposition, animate, Easing, lerp } from "@/components/hero/stage";

/* Authored composition — "put it down here": a light that breathes, three unspoken
   thoughts that drift in, sink into it, and dissolve into ripples. */

const W = 1000, H = 1000;
const C = { x: 500, y: 520 };
const SCENES = [
  { name: "Arrive", dur: 5 },
  { name: "Carry", dur: 6 },
  { name: "Release", dur: 8 },
  { name: "Rest", dur: 8 },
];

const MOTION = { drift: Easing.easeInOutSine, settle: Easing.easeOutCubic, sink: Easing.easeInOutCubic };

const THOUGHTS = [
  { text: "the knot in my chest", x: 210, y: 286, w: 320, rot: -6, tone: "light" },
  { text: "what I didn\u2019t say", x: 566, y: 168, w: 300, rot: 5, tone: "deep" },
  { text: "tired of being fine", x: 596, y: 700, w: 310, rot: 7, tone: "light" },
];

const n = (v, d = 0) => (Number.isFinite(v) ? v : d);
const h = (i, s) => { const x = Math.sin(i * 12.9898 + s * 78.233) * 43758.5453; return x - Math.floor(x); };

const MOTES = Array.from({ length: 18 }, (_, i) => ({
  x: 90 + h(i, 1) * 830, laps: 1 + Math.round(h(i, 2)), phase: h(i, 3),
  r: 2 + h(i, 4) * 3.2, sway: 14 + h(i, 5) * 26, warm: h(i, 6) > 0.62,
}));

const EMBERS = Array.from({ length: 7 }, (_, i) => ({
  dx: (h(i, 7) - 0.5) * 190, phase: h(i, 8), r: 2.5 + h(i, 9) * 2.6, sway: 16 + h(i, 10) * 22,
}));

function Piece({ accent = "#5B4A92", onBegin }) {
  const { T: rawT, CUES: rawCues, authoredTotal } = useComposition();
  const CUES = { Arrive: 0, Carry: 5, Release: 11, Rest: 19, ...rawCues };
  const T = Number.isFinite(rawT) ? rawT : 0;
  const total = authoredTotal || 27;
  const A = (o) => animate(o)(T);

  const period = total / 4;
  const breath = Math.sin((T / period) * Math.PI * 2 - Math.PI / 2);

  let lift = 0;
  const rel = THOUGHTS.map((_, i) => {
    const s = CUES.Release + i * 2.3;
    return { s, e: s + 2.5 };
  });
  rel.forEach((r) => { lift += A({ from: 0, to: 0.13, start: r.e - 1.2, end: r.e + 0.9, ease: MOTION.settle }); });
  const bloom = A({ from: 0, to: 0.3, start: CUES.Rest + 0.3, end: CUES.Rest + 3.4, ease: MOTION.settle });
  let flare = 0;
  rel.forEach((r) => {
    const f = A({ from: 0, to: 1, start: r.e - 0.5, end: r.e + 1.7, ease: Easing.easeOutQuad });
    if (f > 0.001 && f < 0.999) flare += Math.sin(Math.pow(f, 0.6) * Math.PI) * 0.5;
  });
  const reset = 1 - A({ from: 0, to: 1, start: total - 4.6, end: total - 0.05, ease: MOTION.drift });
  const I = 1 + (lift + bloom) * reset;

  const glow = (size, bg, mul, op) => ({
    position: "absolute", left: n(C.x - size / 2), top: n(C.y - size / 2), width: n(size), height: n(size),
    borderRadius: "50%", background: bg,
    opacity: Math.min(1.35, op * I + flare * (mul / 2.4) * 0.4),
    transform: "scale(" + (1 + breath * 0.045 * mul + flare * 0.05 * mul) * (0.94 + I * 0.06) + ")",
    willChange: "transform, opacity",
  });

  const restGate = A({ from: 0, to: 1, start: CUES.Rest, end: CUES.Rest + 1.6, ease: MOTION.settle })
    * (1 - A({ from: 0, to: 1, start: total - 2.6, end: total - 0.3, ease: MOTION.drift }));

  return (
    <div style={{ position: "absolute", inset: 0, fontFamily: "Outfit, Poppins, Helvetica, sans-serif" }}>
      <div style={glow(860, "radial-gradient(circle, rgba(255,238,210,0.50) 0%, rgba(236,214,242,0.26) 34%, rgba(206,192,236,0) 68%)", 1, 0.9)} />
      <div style={glow(420, "radial-gradient(circle, rgba(255,250,240,0.72) 0%, rgba(250,226,232,0.34) 45%, rgba(230,214,244,0) 72%)", 1.5, 0.9)} />
      <div style={glow(132, "radial-gradient(circle, rgba(255,255,255,0.96) 0%, rgba(255,246,226,0.72) 48%, rgba(255,240,220,0) 78%)", 2.4, 1)} />

      {[0, 1, 2].map((k) => {
        const f = (T / period + k / 3) % 1;
        const d = lerp(150, 900, Math.pow(f, 0.72));
        return (
          <div key={"r" + k} style={{
            position: "absolute", left: n(C.x - d / 2), top: n(C.y - d / 2), width: n(d, 1), height: n(d, 1),
            borderRadius: "50%", border: "1px solid " + accent,
            opacity: Math.pow(1 - f, 1.7) * 0.3 * Math.min(1, f * 14),
          }} />
        );
      })}

      <div style={{
        position: "absolute", left: C.x - 330, top: C.y - 330, width: 660, height: 660,
        borderRadius: "50%", border: "1px dashed rgba(91,74,146,0.16)",
        transform: "rotate(" + (T / total) * 360 + "deg)",
      }} />

      {rel.map((r, i) => {
        const f = A({ from: 0, to: 1, start: r.e - 0.7, end: r.e + 2.6, ease: Easing.easeOutQuad });
        if (f <= 0.001 || f >= 0.999) return null;
        const d = lerp(60, 780, f);
        return (
          <div key={"ri" + i} style={{
            position: "absolute", left: n(C.x - d / 2), top: n(C.y - d / 2), width: n(d, 1), height: n(d, 1), borderRadius: "50%",
            border: (1 + (1 - f) * 2.6) + "px solid rgba(255,246,228," + (1 - f) * 0.95 + ")",
            boxShadow: "0 0 44px rgba(255,232,192," + (1 - f) * 0.55 + "), inset 0 0 60px rgba(255,236,205," + (1 - f) * 0.18 + ")",
          }} />
        );
      })}

      {MOTES.map((m, i) => {
        const p = (m.phase + (T / total) * m.laps) % 1;
        const y = lerp(1010, -40, p);
        const x = m.x + Math.sin(p * Math.PI * 2 * m.laps + i) * m.sway;
        return (
          <div key={"m" + i} style={{
            position: "absolute", left: n(x), top: n(y), width: m.r * 2, height: m.r * 2, borderRadius: "50%",
            background: m.warm ? "rgba(255,232,196,0.9)" : "rgba(255,255,255,0.85)",
            opacity: Math.sin(p * Math.PI) * 0.5,
          }} />
        );
      })}

      {EMBERS.map((e, i) => {
        const p = (e.phase + (T / total) * 2) % 1;
        const y = C.y - 40 - p * 430;
        const x = C.x + e.dx * (0.3 + p * 0.7) + Math.sin(p * Math.PI * 3 + i) * e.sway;
        return (
          <div key={"e" + i} style={{
            position: "absolute", left: n(x), top: n(y), width: e.r * 2, height: e.r * 2, borderRadius: "50%",
            background: "rgba(255,228,186,0.95)", boxShadow: "0 0 12px rgba(255,224,180,0.8)",
            opacity: Math.sin(p * Math.PI) * 0.6 * restGate,
          }} />
        );
      })}

      {(() => {
        const rise = A({ from: 0, to: 1, start: CUES.Rest + 0.5, end: CUES.Rest + 3.0, ease: MOTION.settle });
        const out = A({ from: 0, to: 1, start: total - 2.4, end: total - 0.6, ease: MOTION.drift });
        const vis = rise * (1 - out);
        if (vis <= 0.002) return null;
        const w = 470;
        const up = lerp(30, -200, rise) + Math.sin((T / period) * Math.PI * 2) * 5;
        return (
          <div style={{
            position: "absolute", left: n(C.x - w / 2), top: n(C.y + up), width: w, padding: "32px 34px 34px",
            borderRadius: 28,
            background: "linear-gradient(150deg, rgba(255,255,255,0.96) 0%, rgba(255,248,238,0.9) 100%)",
            border: "1px solid rgba(255,255,255,0.9)",
            boxShadow: "0 34px 70px -26px rgba(70,48,110,0.4), 0 0 60px rgba(255,232,190," + 0.5 * vis + ")",
            opacity: vis, filter: rise < 0.99 ? "blur(" + (1 - rise) * 9 + "px)" : "none",
            transform: "scale(" + lerp(0.52, 1, rise) + ")", transformOrigin: "50% 100%",
            willChange: "transform, opacity", pointerEvents: vis > 0.9 ? "auto" : "none",
          }}>
            <div style={{ fontSize: 36, fontWeight: 500, color: "#2F2252", letterSpacing: "-0.02em", lineHeight: 1.14 }}>
              You’re not carrying<br />this alone.
            </div>
            <div style={{ fontSize: 17, fontWeight: 300, color: "rgba(74,58,114,0.62)", marginTop: 14, lineHeight: 1.45 }}>
              Four minutes. Nobody sees this but you.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 24 }}>
              <button onClick={onBegin} data-testid="mental-hero-anim-cta" style={{
                padding: "15px 26px", borderRadius: 999, cursor: "pointer", border: "none",
                background: "linear-gradient(135deg,#7B67B6 0%,#57468E 100%)",
                color: "#fff", fontSize: 18, fontWeight: 500, display: "flex", alignItems: "center", gap: 12,
                boxShadow: "0 16px 32px -14px rgba(60,40,100,0.6)", fontFamily: "inherit",
              }}>
                <span>Take the first minute</span><span>&#8594;</span>
              </button>
            </div>
          </div>
        );
      })()}

      {THOUGHTS.map((c, i) => {
        const inS = CUES.Carry + i * 1.15, inE = inS + 3.0;
        const r = rel[i];
        const app = A({ from: 0, to: 1, start: inS, end: inE, ease: MOTION.settle });
        const sink = A({ from: 0, to: 1, start: r.s, end: r.e, ease: MOTION.sink });
        const fade = 1 - A({ from: 0, to: 1, start: r.s + 1.15, end: r.e - 0.05, ease: MOTION.drift });
        const bob = Math.sin((T / period) * Math.PI * 2 + i * 2.1) * 10 * app * (1 - sink);
        const x = lerp(c.x, C.x - c.w / 2, sink);
        const y = lerp(lerp(c.y - 250, c.y, app), C.y - 46, sink) + bob;
        const deep = c.tone === "deep";
        return (
          <div key={"c" + i} style={{
            position: "absolute", left: n(x), top: n(y), width: c.w, padding: "20px 26px", borderRadius: 22,
            display: "flex", alignItems: "center", gap: 14,
            background: deep ? "linear-gradient(135deg,#7B67B6 0%,#57468E 100%)" : "linear-gradient(135deg,rgba(255,255,255,0.88) 0%,rgba(255,255,255,0.62) 100%)",
            border: "1px solid " + (deep ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.75)"),
            boxShadow: deep ? "0 26px 50px -20px rgba(52,34,92,0.55)" : "0 22px 46px -22px rgba(72,50,112,0.38)",
            color: deep ? "rgba(255,255,255,0.96)" : "#4A3A72",
            fontSize: 28, fontWeight: 400, lineHeight: 1.25, letterSpacing: "-0.01em",
            opacity: app * fade, filter: sink > 0.01 ? "blur(" + sink * 6 + "px)" : "none",
            transform: "rotate(" + lerp(c.rot, 0, sink) + "deg) scale(" + lerp(1, 0.44, sink) + ")",
            transformOrigin: "50% 50%", willChange: "transform, opacity",
          }}>
            <div style={{ width: 9, height: 9, borderRadius: "50%", flex: "0 0 auto", background: deep ? "rgba(255,226,186,0.95)" : "rgba(123,103,182,0.75)" }} />
            <div>{c.text}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function MentalHero({ accent = "#5B4A92", onBegin }) {
  return (
    <CompositionStage width={W} height={H} scenes={SCENES} minScale={0.5} maxHeight={620} style={{ maxWidth: 880, marginLeft: "auto", marginRight: -40 }}>
      <Piece accent={accent} onBegin={onBegin} />
    </CompositionStage>
  );
}
